<template>
  <UiCard :title="title || t('mailClient.record.title')" flush>
    <template #actions>
      <UiButton v-if="canSend" size="sm" variant="secondary" @click="send">{{ t('mailClient.record.send') }}</UiButton>
    </template>

    <!-- Las conversaciones con la forma de su fila (asunto y fecha, y debajo
         quién), solo en la primera carga: al volver de enviar se queda la lista. -->
    <ul v-if="loading && !items.length" class="rm__list" role="status" :aria-label="t('common.loading')" aria-busy="true">
      <li v-for="width in ['70%', '55%', '64%']" :key="width" class="rm__row" aria-hidden="true">
        <span class="rm__subject sk-line">&#8203;<UiSkeleton :width="width" /></span>
        <span class="rm__meta sk-line">&#8203;<UiSkeleton width="40%" /></span>
        <span class="rm__date sk-line sk-line--date">&#8203;<UiSkeleton width="100%" /></span>
      </li>
    </ul>
    <p v-else-if="failed" class="rm__pad rm__soft">{{ t('mailClient.record.failed') }}</p>
    <p v-else-if="!items.length" class="rm__pad rm__soft">{{ t('mailClient.record.none') }}</p>
    <ul v-else class="rm__list">
      <li v-for="item in items" :key="item.id">
        <RouterLink :to="item.route" class="rm__row tap">
          <span class="rm__subject">{{ item.title }}</span>
          <span class="rm__meta">{{ item.subtitle }}</span>
          <span class="rm__date">{{ when(item.date) }}</span>
        </RouterLink>
      </li>
    </ul>
    <p v-if="total > items.length" class="rm__pad rm__soft">{{ t('mailClient.record.more', { n: total - items.length }) }}</p>

    <!-- Los archivos que llegaron por correo y se relacionaron con ESTA ficha,
         uno a uno: pueden venir de conversaciones que no lo están. -->
    <template v-if="files.length">
      <p class="rm__sub">{{ t('mailClient.record.files', { n: filesTotal }) }}</p>
      <ul class="rm__list">
        <li v-for="item in files" :key="item.id">
          <RouterLink :to="item.route" class="rm__row tap">
            <span class="rm__subject"><span class="rm__ext">{{ item.badge }}</span>{{ item.title }}</span>
            <span class="rm__meta">{{ item.subtitle }}</span>
            <span class="rm__date">{{ when(item.date) }}</span>
          </RouterLink>
        </li>
      </ul>
    </template>
  </UiCard>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiButton, UiCard, UiSkeleton } from '@general/ui-kit'
import { useAuthStore } from '@general/auth/frontend'

import { useMailStore } from './store'
import { relatedFiles, relatedPanel } from './related'

/**
 * The conversations about one record — and the files that came by email and
 * were linked to it one by one — on that record's own page — for the
 * pages that have no «related» tabs (an invoice, a quote, an order). Where the
 * page has tabs, the same list is the platform's «Comunicaciones» tab and this
 * is not needed.
 *
 * Not a copy of anything: it asks the platform which conversations are linked
 * to this record, the same answer everywhere. And «Send by email» opens the
 * composer on the record, with whatever the owning module prepares (the PDF,
 * the customer, the subject).
 */
const props = defineProps({
  type: { type: String, required: true },
  referenceId: { type: String, required: true },
  title: { type: String, default: '' },
  /** Whether this record can be sent from here (the module registered a context). */
  sendable: { type: Boolean, default: true },
  /** What the page does once the email has gone (reload the record, its status). */
  onSent: { type: Function, default: null },
})
const { t, locale } = useI18n()
const auth = useAuthStore()
const store = useMailStore()

const loading = ref(true)
const failed = ref(false)
const items = ref([])
const total = ref(0)
const files = ref([])
const filesTotal = ref(0)

const canSend = computed(() => props.sendable && (auth.can?.('mail:use') ?? false))

async function load() {
  loading.value = true
  failed.value = false
  try {
    const [data, attached] = await Promise.all([
      relatedPanel(props.type, props.referenceId, 10),
      // A files panel that fails only takes the files away, not the card.
      relatedFiles(props.type, props.referenceId, 20).catch(() => ({ items: [] })),
    ])
    items.value = data.items ?? []
    total.value = data.total ?? items.value.length
    failed.value = Boolean(data.failed)
    files.value = attached.items ?? []
    filesTotal.value = attached.total ?? files.value.length
  } catch {
    failed.value = true
  } finally {
    loading.value = false
  }
}

function send() {
  store.compose({
    mode: 'new',
    context: { type: props.type, referenceId: String(props.referenceId) },
    onSent: (result) => {
      load()
      props.onSent?.(result)
    },
  })
}

function when(value) {
  return value ? new Date(value).toLocaleDateString(locale.value, { day: 'numeric', month: 'short', year: 'numeric' }) : ''
}

watch(() => store.changed, load)
watch(() => [props.type, props.referenceId], load)
onMounted(load)
</script>

<style scoped>
.rm__pad { margin: 0; padding: var(--space-3) var(--space-4); }
.rm__soft { color: var(--color-text-soft); font-size: var(--text-sm); }
.rm__list { list-style: none; margin: 0; padding: 0; }
.rm__row { display: grid; grid-template-columns: 1fr auto; gap: 2px var(--space-3); padding: 10px var(--space-4); border-top: 1px solid var(--color-border); color: var(--color-text); text-decoration: none; }
.rm__row:hover { background: var(--color-surface-2); }
.rm__subject { font-size: var(--text-sm); font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rm__meta { grid-column: 1; font-size: var(--text-xs); color: var(--color-text-soft); }
.rm__date { grid-row: 1; grid-column: 2; font-size: var(--text-xs); color: var(--color-text-soft); }
.rm__sub { margin: 0; padding: var(--space-3) var(--space-4) var(--space-1); border-top: 1px solid var(--color-border); font-size: var(--text-xs); font-weight: 600; color: var(--color-text-soft); text-transform: uppercase; letter-spacing: .04em; }
/* Cada línea del esqueleto mide lo que su texto: un espacio de ancho cero da
   el alto y la barra gris va encima. */
.sk-line { position: relative; display: block; }
.sk-line > :deep(.skeleton) { position: absolute; left: 0; top: 50%; translate: 0 -50%; }
.sk-line--date { width: 5rem; }
.rm__ext { display: inline-block; min-width: 32px; margin-right: 8px; padding: 1px 4px; border-radius: 4px; background: var(--color-primary-soft); color: var(--color-primary); font-size: 10px; font-weight: 700; text-align: center; }
</style>
